/**
 * R6.3 PHASE 2 — real-data read model for the SOVERDI CGM runtime.
 *
 * This is the single bridge to Agri Safe's canonical live read layer. Missing
 * live inventory never becomes a fabricated zero, a false NOT_COMMISSIONED
 * state or a guessed valve/pump state.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFarmOperationalData } from '../../hooks/useFarmOperationalData';
import { useFieldBindings } from '../../hooks/useFieldBindings';
import { iotApi, type IoTAlert, type IoTNode, type ParcelValveBinding, type PumpBindingView } from '../../lib/iotApi';
import type { CgmTopology } from '../../config/cgmPilot';
import type { SectorPlacement } from '../../lib/operationalOverview';
import { isValveCapable } from '../../lib/liveSensorMetrics';
import { buildSiteModel, nodesForSite, type SiteOperationalModel } from '../../lib/siteOperationalModel';
import {
  buildSoverdiSectorViews,
  toSoverdiAlerts,
  toSoverdiDashboardView,
  type SoverdiDashboardView,
  type SoverdiSectorView,
} from '../adapters/agriSafeDashboardAdapter';
import { useSectorDisplayNames } from '../../hooks/useSectorDisplayNames';
import { buildInstalledSectorViews, bindingDisplayName } from '../adapters/installedSectors';

const ALERTS_POLL_MS = 30_000;
const CLOCK_TICK_MS = 10_000;

export interface AgriSafeSoverdiDashboard {
  view: SoverdiDashboardView | null;
  sectors: SoverdiSectorView[];
  operationalSectors?: SoverdiSectorView[];
  pressureSensorByBinding?: Record<string, string>;
  technicalSectors?: SoverdiSectorView[];
  parcelDisplayName?: (binding: ParcelValveBinding) => string;
  siteModels: SiteOperationalModel[];
  loading: boolean;
  loadError: boolean;
  /** True only after the canonical node inventory has been read successfully. */
  nodesLoaded: boolean;
  placementUnavailable: boolean;
  bindingsUnavailable: boolean;
  nodes: IoTNode[];
  placement: Map<string, SectorPlacement>;
  topology: CgmTopology | null;
  /** Canonical commissioning authority used by semantic parcel controls. */
  parcelBindings: ParcelValveBinding[];
  /** All canonical pump actuators, including multiple actuators on one site. */
  pumps: PumpBindingView[];
  /** Immediate proving read after a physical command. */
  refreshNodes: () => Promise<IoTNode[]>;
  renameSector: (siteName: string, sectorCode: string, name: string | null, bindingId?: string) => Promise<void>;
  namesUnavailable: boolean;
}

const withoutLiveSectorEvidence = (sector: SoverdiSectorView): SoverdiSectorView => ({
  ...sector,
  status: 'UNKNOWN',
  operationalState: 'unknown',
  pressureBar: null,
  pressureFreshness: 'absent',
  flowM3h: null,
  flowFreshness: 'absent',
  soilMoisturePercent: null,
  soilMoistureFreshness: 'absent',
  valveState: 'unknown',
  stateSinceMs: null,
});

const withoutLiveSiteEvidence = (site: SiteOperationalModel): SiteOperationalModel => ({
  ...site,
  relayNode: undefined,
  relayChannels: undefined,
  operationalState: 'unknown',
  stateEvidence: 'none',
  stateSinceMs: null,
  lastConfirmationMs: null,
  relayAirTemperature: { ...site.relayAirTemperature, value: null, freshness: 'absent' },
  relayAirHumidity: { ...site.relayAirHumidity, value: null, freshness: 'absent' },
  lastCommunicationMs: null,
  siteNodes: [],
});

export function useAgriSafeSoverdiDashboard(): AgriSafeSoverdiDashboard {
  const {
    topology,
    nodes,
    nodesLoaded,
    placementLoaded,
    placementError,
    placement,
    loading,
    loadError,
    refreshNodes,
  } = useFarmOperationalData();
  const {
    parcelBindings,
    farmId: bindingsFarmId,
    pumps,
    pressureSensorByBinding,
    loaded: bindingsLoaded,
    error: bindingsError,
  } = useFieldBindings();

  const [alerts, setAlerts] = useState<IoTAlert[]>([]);
  const [alertsUnavailable, setAlertsUnavailable] = useState(false);
  const alertsRequestGeneration = useRef(0);
  const [, setTick] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => setTick((value) => value + 1), CLOCK_TICK_MS);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    let mounted = true;
    let timer: number | undefined;
    const load = async (): Promise<void> => {
      const requestGeneration = ++alertsRequestGeneration.current;
      try {
        const rows = await iotApi.getJson<IoTAlert[]>('/iot/alerts');
        if (!mounted || requestGeneration !== alertsRequestGeneration.current) return;
        setAlerts(Array.isArray(rows) ? rows : []);
        setAlertsUnavailable(false);
      } catch {
        if (!mounted || requestGeneration !== alertsRequestGeneration.current) return;
        // Once freshness of the latest alert feed read is lost, retained rows cannot be
        // presented as current priorities. Fail closed until a fresh read succeeds.
        setAlerts([]);
        setAlertsUnavailable(true);
      } finally {
        // Automatic polling is serialized: the next read starts only after this one settles.
        if (mounted && requestGeneration === alertsRequestGeneration.current) {
          timer = window.setTimeout(() => { void load(); }, ALERTS_POLL_MS);
        }
      }
    };
    void load();
    return () => {
      mounted = false;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, []);

  const farmId = topology?.farmId ?? '';
  const { customNames, renameSector, namesUnavailable } = useSectorDisplayNames(farmId);

  const now = Date.now();
  const liveEvidenceAvailable = nodesLoaded && placementLoaded && !placementError;
  const bindingEvidenceAvailable = bindingsLoaded && !bindingsError && bindingsFarmId === farmId;

  const sectors = useMemo<SoverdiSectorView[]>(() => {
    if (!topology) return [];
    const built = buildSoverdiSectorViews({ topology, nodes, placement, now, customNames });
    return liveEvidenceAvailable ? built : built.map(withoutLiveSectorEvidence);
  }, [topology, nodes, placement, now, customNames, liveEvidenceAvailable]);

  const operationalSectors = useMemo(() => buildInstalledSectorViews({
    sectors, placement, bindings: bindingEvidenceAvailable ? parcelBindings : [],
    nodes: liveEvidenceAvailable ? nodes : [], pressureSensorByBinding: pressureSensorByBinding ?? {},
    customNames, now,
  }), [sectors, placement, parcelBindings, bindingEvidenceAvailable, nodes, liveEvidenceAvailable, pressureSensorByBinding, customNames, now]);
  const parcelDisplayName = useCallback((binding: ParcelValveBinding) => bindingDisplayName(binding, customNames), [customNames]);

  const siteModels = useMemo<SiteOperationalModel[]>(() => {
    if (!topology) return [];
    const built = topology.sites.map((site) => {
      const controllers = nodesForSite(nodes, placement, site.name).filter((node) =>
        isValveCapable(node.model),
      );
      return buildSiteModel({
        siteName: site.name,
        nodes,
        placement,
        pumps,
        controllers,
        now,
      });
    });
    return liveEvidenceAvailable && bindingEvidenceAvailable ? built : built.map(withoutLiveSiteEvidence);
  }, [topology, nodes, placement, pumps, now, liveEvidenceAvailable, bindingEvidenceAvailable]);

  const view = useMemo<SoverdiDashboardView | null>(() => {
    if (!topology) return null;
    const total = operationalSectors.length;
    const active = liveEvidenceAvailable
      ? operationalSectors.filter((sector) => sector.operationalState === 'active').length
      : 0;
    return toSoverdiDashboardView({
      farmName: topology.clientName,
      sectors: operationalSectors,
      kpis: { activeSectors: active, totalSectors: total },
      alerts: toSoverdiAlerts(alerts, alertsUnavailable),
      alertsUnavailable,
      upcomingIrrigations: [],
    });
  }, [topology, operationalSectors, liveEvidenceAvailable, alerts, alertsUnavailable]);

  // Topology can resolve before the first node inventory request starts. Keep
  // the operational UI in its loading state until that first request either
  // succeeds or fails, avoiding a transient false NOT_COMMISSIONED snapshot.
  const operationalLoading = loading
    || Boolean(topology && !nodesLoaded && !loadError)
    || Boolean(topology && !placementLoaded && !placementError)
    || Boolean(topology && !bindingsLoaded && !bindingsError);

  return {
    view,
    sectors: operationalSectors,
    technicalSectors: sectors,
    pressureSensorByBinding: bindingEvidenceAvailable ? pressureSensorByBinding : {},
    operationalSectors,
    parcelDisplayName,
    siteModels,
    loading: operationalLoading,
    loadError,
    nodesLoaded,
    placementUnavailable: placementError,
    bindingsUnavailable: !bindingEvidenceAvailable,
    nodes,
    placement,
    topology,
    parcelBindings: bindingEvidenceAvailable ? parcelBindings : [],
    pumps: bindingEvidenceAvailable ? pumps : [],
    refreshNodes,
    renameSector,
    namesUnavailable,
  };
}
